import lodash from 'lodash';
import pDefer from 'p-defer';
import { deconstructPairId } from '../../utils.js';
const { chunk } = lodash;
class AssetAvailabilityService {
    #assetsService;
    #refreshIntervalMs;
    #refreshIntervalId;
    #loadedDeferred = pDefer();
    #started = false;
    #currentAssetIds;
    #currentEnabledSources;
    #indirectAssetIds = new Set();
    #chainedFromAssetIds = new Set();
    #chainedToAssetIds = new Set();
    #exchangeableFromAssetIds = new Set();
    #directPairs = [];
    constructor({ assetsService, refreshIntervalMs }) {
        this.#assetsService = assetsService;
        this.#refreshIntervalMs = refreshIntervalMs || 60 * 1000 * 5;
    }
    async getSupportedFromAssetIds() {
        await this.#loadedDeferred.promise;
        return new Set(this.#exchangeableFromAssetIds);
    }
    async getSupportedToAssetIds(from) {
        await this.#loadedDeferred.promise;
        const walletIds = new Set();
        this.#directPairs
            .filter((pair) => pair.from === from)
            .forEach((pair) => {
            if (pair.to !== from)
                walletIds.add(pair.to);
        });
        if (this.#indirectAssetIds.has(from)) {
            this.#indirectAssetIds.forEach((assetId) => {
                if (assetId !== from)
                    walletIds.add(assetId);
            });
        }
        if (this.#chainedFromAssetIds.has(from)) {
            this.#chainedToAssetIds.forEach((assetId) => {
                if (assetId !== from)
                    walletIds.add(assetId);
            });
        }
        return walletIds;
    }
    async isAssetExchangeable(assetId) {
        await this.#loadedDeferred.promise;
        const isAssetExchangeableFrom = this.#exchangeableFromAssetIds.has(assetId);
        const isDirectTo = this.#directPairs.some((pair) => pair.to === assetId);
        const isAssetExchangeableTo = isDirectTo ||
            this.#indirectAssetIds.has(assetId) ||
            this.#chainedToAssetIds.has(assetId);
        return isAssetExchangeableFrom || isAssetExchangeableTo;
    }
    async isPairSupportedBySource(source, pairId) {
        await this.#loadedDeferred.promise;
        const { from, to } = deconstructPairId(pairId);
        if (from === to)
            return false;
        if (source === 'direct') {
            return this.#directPairs.some((pair) => pair.from === from && pair.to === to);
        }
        if (source === 'indirect') {
            return this.#indirectAssetIds.has(from) && this.#indirectAssetIds.has(to);
        }
        return this.#chainedFromAssetIds.has(from) && this.#chainedToAssetIds.has(to);
    }
    async isExactOutSupported(pairId) {
        await this.#loadedDeferred.promise;
        const { from, to } = deconstructPairId(pairId);
        if (from === to)
            return false;
        const isIndirectRoutable = this.#indirectAssetIds.has(from) && this.#indirectAssetIds.has(to);
        if (isIndirectRoutable)
            return false;
        return this.#directPairs.some((pair) => pair.from === from && pair.to === to);
    }
    async start(assetIds, enabledSources) {
        this.#currentAssetIds = assetIds;
        this.#currentEnabledSources = enabledSources;
        await this.#buildExchangeableAssets(assetIds, enabledSources);
        if (this.#refreshIntervalMs > 0) {
            this.#refreshIntervalId = setInterval(() => {
                if (!this.#currentAssetIds || !this.#currentEnabledSources) {
                    return;
                }
                void this.#buildExchangeableAssets(this.#currentAssetIds, this.#currentEnabledSources).catch((err) => {
                    console.error('Failed to refresh exchangeable assets:', err);
                });
            }, this.#refreshIntervalMs);
        }
        this.#started = true;
    }
    async update(assetIds, enabledSources) {
        if (!this.#started) {
            console.warn('AssetAvailabilityService not started, ignoring update');
            return;
        }
        if (lodash.isEqual(enabledSources, this.#currentEnabledSources) &&
            lodash.isEqual(assetIds, this.#currentAssetIds)) {
            return;
        }
        this.#currentAssetIds = assetIds;
        this.#currentEnabledSources = enabledSources;
        await this.#buildExchangeableAssets(assetIds, enabledSources);
    }
    stop() {
        if (this.#refreshIntervalId) {
            clearInterval(this.#refreshIntervalId);
            this.#refreshIntervalId = undefined;
        }
    }
    async #buildExchangeableAssets(assetIds, enabledSources) {
        const [directData, indirectData, chainedData] = await Promise.all([
            this.#fetchDirectPairs(enabledSources),
            this.#fetchIndirectAssets(assetIds, enabledSources),
            this.#fetchChainedAssets(assetIds, enabledSources),
        ]);
        const response = {
            direct: directData,
            indirect: indirectData,
            chained: chainedData,
        };
        this.#populateState(response);
        this.#loadedDeferred.resolve();
    }
    #populateState(data) {
        this.#directPairs = data.direct.pairs;
        this.#indirectAssetIds = data.indirect.assetIds;
        this.#chainedFromAssetIds = data.chained.fromAssetIds;
        this.#chainedToAssetIds = data.chained.toAssetIds;
        this.#exchangeableFromAssetIds = new Set([
            ...data.direct.fromAssetIds,
            ...data.indirect.assetIds,
            ...data.chained.fromAssetIds,
        ]);
    }
    async #fetchDirectPairs(enabledSources) {
        if (!enabledSources.includes('direct')) {
            return { fromAssetIds: new Set(), pairs: [] };
        }
        try {
            const pairs = await this.#assetsService.getPairs();
            const fromAssetIds = new Set(pairs.map((pair) => pair.from));
            return {
                fromAssetIds,
                pairs: pairs.map((pair) => ({ from: pair.from, to: pair.to })),
            };
        }
        catch (err) {
            console.error('Failed to fetch direct pairs:', err);
            const fromAssetIds = new Set(this.#directPairs.map((pair) => pair.from));
            return { fromAssetIds, pairs: [...this.#directPairs] };
        }
    }
    async #fetchIndirectAssets(assetIds, enabledSources) {
        if (!enabledSources.includes('indirect')) {
            return { assetIds: new Set(), sources: [] };
        }
        const batchSize = 150;
        const batches = chunk([...assetIds].sort((a, b) => a.localeCompare(b)), batchSize);
        const allAssetIds = new Set();
        const errors = [];
        await Promise.all(batches.map(async (batch) => {
            try {
                const result = await this.#assetsService.getAssetSources(['indirect'], batch);
                result.forEach((source) => {
                    source.assetIds.forEach((id) => allAssetIds.add(id));
                });
            }
            catch (err) {
                errors.push(err);
                console.error('Failed to fetch indirect assets:', err);
            }
        }));
        if (errors.length > 0 && allAssetIds.size === 0) {
            return { assetIds: new Set(this.#indirectAssetIds), sources: ['indirect'] };
        }
        return {
            assetIds: allAssetIds,
            sources: ['indirect'],
        };
    }
    async #fetchChainedAssets(assetIds, enabledSources) {
        if (!enabledSources.includes('chained')) {
            return {
                fromAssetIds: new Set(),
                toAssetIds: new Set(),
            };
        }
        const batchSize = 150;
        const batches = chunk([...assetIds].sort((a, b) => a.localeCompare(b)), batchSize);
        const fromAssetIds = new Set();
        const toAssetIds = new Set();
        const fromErrors = [];
        const toErrors = [];
        await Promise.all([
            Promise.all(batches.map(async (batch) => {
                try {
                    const result = await this.#assetsService.getAssetSources(['chained'], batch, 'from');
                    result.forEach((source) => {
                        source.assetIds.forEach((id) => fromAssetIds.add(id));
                    });
                }
                catch (err) {
                    fromErrors.push(err);
                    console.error('Failed to fetch chained FROM assets:', err);
                }
            })),
            Promise.all(batches.map(async (batch) => {
                try {
                    const result = await this.#assetsService.getAssetSources(['chained'], batch, 'to');
                    result.forEach((source) => {
                        source.assetIds.forEach((id) => toAssetIds.add(id));
                    });
                }
                catch (err) {
                    toErrors.push(err);
                    console.error('Failed to fetch chained TO assets:', err);
                }
            })),
        ]);
        return {
            fromAssetIds: fromErrors.length > 0 && fromAssetIds.size === 0
                ? new Set(this.#chainedFromAssetIds)
                : fromAssetIds,
            toAssetIds: toErrors.length > 0 && toAssetIds.size === 0
                ? new Set(this.#chainedToAssetIds)
                : toAssetIds,
        };
    }
}
export default AssetAvailabilityService;
